import { useMemo, useState, useRef, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

// Assets
import IMagnifier from "assets/i-magnifier.js";
import IInfo from "assets/i-info";

// Component
import CardListing from "components/molecules/sofia-magic-studio/card-listing";
import ModalLoader from "components/organism/ModalLoader";
import Tab from "components/molecules/sofia-magic-studio/tab";
import ModalDetailListing from "components/molecules/shared/modal-detail-listing";
import { useDebounce } from "hooks/useDebounce";

// API
import { useGetListListingSofiaInfinite } from "api/sofia-magic-studio";

const ListListing = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [selectedListing, setSelectedListing] = useState(null);
  const [isOpenModalDetailListing, setIsOpenModalDetailListing] =
    useState(false);
  const [search, setSearch] = useState("");

  const isTabRapikan = searchParams.get("sofia_processed") === "true";
  const [params, setParams] = useState({
    _limit: 10,
    q: "",
    sofia_processed: isTabRapikan,
  });

  const observerTarget = useRef(null);

  const {
    data: listListingData,
    isLoading: isLoadingListListing,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch: refetchListListing,
  } = useGetListListingSofiaInfinite({ params });

  const listingData = useMemo(() => {
    if (!listListingData?.pages) return [];
    return listListingData.pages.flatMap((page) => page?.data?.data || []);
  }, [listListingData]);

  const { debounceFn } = useDebounce({
    onChange: (query) => {
      setParams((val) => ({
        ...val,
        q: query,
      }));
    },
    delay: 500,
  });

  const handleSearch = (e) => {
    setSearch(e.target.value);
    debounceFn(e.target.value);
  };

  const handleObserver = useCallback(
    (entries) => {
      const [target] = entries;
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  useEffect(() => {
    const element = observerTarget.current;
    if (!element) return;

    const option = { threshold: 0, rootMargin: "100px" };
    const observer = new IntersectionObserver(handleObserver, option);

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [handleObserver, hasNextPage]);

  const handleToTidiedUpPhoto = (slug) => {
    navigate(
      `/sofia-magic-studio/tidied-up-photo?slug=${slug}&sofia_processed=${params.sofia_processed}`,
    );
  };

  const handleNavigateTab = (tab) => {
    const newParams = {
      _limit: 10,
      q: "",
      sofia_processed: tab === "processed",
    };

    setParams(newParams);
    setSearch("");

    // Reset infinite query to start from page 1
    queryClient.resetQueries({
      queryKey: ["get-list-listing-sofia-infinite"],
      exact: false,
    });

    navigate(`/sofia-magic-studio?sofia_processed=${tab === "processed"}`, {
      replace: true,
    });
  };

  const handleOpenModalDetailListing = (listing) => {
    setSelectedListing(listing);
    setIsOpenModalDetailListing(true);
  };

  return (
    <>
      <div className="min-h-[calc(100vh-81px-73px-48px)] rounded-[4px] bg-white">
        <div className="flex items-center gap-1 px-4 py-2 bg-[#FFF7E0] rounded-t-[4px] border-[0.5px] border-solid border-[#FFD452]">
          <IInfo color="#7A5C00" width={16} height={16} />
          <p className="text-[13px] tracker-[-0.4px] leading-[21.034px] font-inter text-[#7A5C00]">
            Sofia Magic Studio masih dalam tahap beta dan eksperimental. Hasil
            perapihan foto dapat berbeda pada beberapa kondisi.
          </p>
        </div>
        <div className="px-4 py-2 flex items-center justify-between border-b border-solid border-[#D8D8D9]">
          <p className="text-[13px] tracker-[-0.4px] leading-[21.034px] font-inter-bold text-[#252526]">
            Pilih listing yang fotonya akan dirapikan.
          </p>
        </div>
        <div className="px-4 py-2 flex items-center justify-between">
          <Tab
            activeTab={params.sofia_processed ? "processed" : "unprocessed"}
            onSelect={handleNavigateTab}
            disabled={isLoadingListListing}
          />
          <div className="h-[32px] p-2 flex justify-center items-center gap-1 rounded-[4px] border-[1px] border-solid border-[#D8D8D9]">
            <IMagnifier />
            <input
              className={`w-[200px] h-[24px] py-2 text-[11px] leading-[17px] tracking-[-0.4px] font-inter text-[#252526] placeholder:text-[#808285]`}
              placeholder="Cari listing dengan ID/Judul"
              value={search}
              onChange={handleSearch}
            />
          </div>
        </div>

        {!!listingData?.length && (
          <div className="px-4 py-2 overflow-x-auto">
            <div className="rounded-[4px] border-[0.5px] border-solid border-[#D8D8D9]">
              {listingData.map((item, index) => (
                <CardListing
                  key={index}
                  onTidiedUpPhoto={() => handleToTidiedUpPhoto(item.slug)}
                  data={item}
                  onOpenDetail={() => handleOpenModalDetailListing(item)}
                  hideIconRapikan={!isTabRapikan}
                />
              ))}
            </div>
          </div>
        )}

        {!!listingData?.length && (
          <div className="px-4 pb-4">
            <div ref={observerTarget} className="h-4" />
            {isFetchingNextPage && (
              <div className="text-center py-4">
                <p className="text-[13px] text-[#808285]">
                  Memuat lebih banyak...
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <ModalDetailListing
        isOpen={isOpenModalDetailListing}
        onClose={() => setIsOpenModalDetailListing(false)}
        slug={selectedListing?.slug}
      />

      <ModalLoader open={false} />
    </>
  );
};

export default ListListing;
